import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  Grid,
  Chip,
  Paper,
  Divider,
  Alert,
} from "@mui/material";
import { get } from "src/utils/networkUtils";
import PlayerLineup from "./PlayerLineup";
import { getLineupFpts } from "./utils";
import { fDateTime } from "src/utils/formatTime";
import Iconify from "../iconify";

export default function VersusGameLineupDialog({ open, onClose, lineupId }) {
  const [lineup, setLineup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && lineupId) {
      fetchLineupById(lineupId);
    }
  }, [open, lineupId]);

  const fetchLineupById = async (id) => {
    setLoading(true);
    setError(null);

    try {
      const response = await get(`/admin/versus/lineups/${id}`);
      if (response.lineup) {
        setLineup(response.lineup);
      } else {
        setError("Lineup not found");
      }
    } catch (error) {
      console.error("Error fetching lineup:", error);
      setError(error.response?.data?.error || "Failed to fetch lineup");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLineup(null);
    setError(null);
    onClose();
  };
  console.log("Lineup ID:", lineup);
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" component="div">
            Versus Lineup Details
          </Typography>
          {lineupId && (
            <Chip
              label={`ID: ${lineupId}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={400}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {lineup && !loading && !error && (
          <Grid container spacing={3}>
            {/* Lineup Information */}
            <Grid item xs={12}>
              <Paper sx={{ px: 3, mb: 1 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Iconify icon="eva:info-outline" sx={{ mr: 1 }} />
                  Lineup Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={1}>
                  <Grid item xs={6} sm={6} md={1}>
                    <Typography variant="body2" color="text.secondary">
                      Score
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {lineup.score || 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={6} md={1}>
                    <Typography variant="body2" color="text.secondary">
                      FPTS
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      color="primary"
                    >
                      {lineup.fpts.toFixed(2) ||
                        getLineupFpts(lineup.cards || [])}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      Record
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {lineup.stats.wins || 0} - {lineup.stats.draws || 0} -{" "}
                      {lineup.stats.losses || 0}
                      {`(${lineup.stats.totalGames || 0})`}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={6} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {lineup.createdAt ? fDateTime(lineup.createdAt) : "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={6} md={1}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={lineup.isIgnored ? "Ignored" : "Active"}
                      color={lineup.isIgnored ? "error" : "success"}
                      size="small"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Player Cards */}
            <Grid item xs={12}>
              <Paper sx={{ px: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Iconify icon="eva:people-outline" sx={{ mr: 1 }} />
                  Player Cards ({lineup.cards?.length || 0})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {lineup.cards && lineup.cards.length > 0 ? (
                  <PlayerLineup lineup={lineup.cards} />
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight={200}
                    sx={{ color: "text.secondary" }}
                  >
                    <Iconify
                      icon="eva:inbox-outline"
                      sx={{ fontSize: 64, mb: 2 }}
                    />
                    <Typography variant="h6">No Cards Found</Typography>
                    <Typography variant="body2">
                      This lineup doesn't have any player cards.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Iconify icon="eva:close-outline" />}
        >
          Close
        </Button>
        {lineup && (
          <Button
            onClick={() => fetchLineupById(lineupId)}
            disabled={loading}
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} />
              ) : (
                <Iconify icon="eva:refresh-outline" />
              )
            }
          >
            Refresh
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
